// 190 §C — THE BENTO OVER TASKS, full screen above everything else.
//
// Laurent (C.4): "show it full screen above everything else like eisenhower,
// COPY that working exactly, don't reinvent." So the overlay below is
// EisenhowerDialog.tsx:133–159's shell — `position: fixed`, `top: headerH - 1`,
// right/bottom 0 and — since 206E §1.1 — `left` at the sidebar's right edge
// (App's `sidebarEdge`, handed down beside `headerH`; it was 0, over the
// sidebar), `background: var(--bs)`, `zIndex: 9999`, `padding: "20px
// 24px"`, `overflow: auto`, a backdrop click that closes and an inner header
// that stops propagation — with `headerH` the ONE App measures (App.tsx:4295,
// in CSS px since 190 glance round 2 [F]; the dialog frame has one host, App,
// and takes the matrix's own number instead of measuring a second time) —
// and the Escape handler App.tsx:1410–1420 gives the matrix, mounted here. The
// template picker is NOT here since 190 [I] (ruled): it sits in the same row
// as the Bento icon, right of it, so the HOST renders it (BentoBoard.tsx's
// BentoPicker) from the `useBento` state the host owns and hands down as
// `bento` — this dialog calls no hook of its own for the board. The cards are
// the matrix's own card, moved to EisenTaskCard; the columns and the drop are
// BentoBoard's.
// NO DRAG STATE HERE (190 glance (i)(a), ruled): a Bento card never dims —
// the card's drag props are simply not passed.
//
// TWO SURFACES MOUNT IT: the user Bento (App's header, my own open tasks,
// per-user placements) and a case's Bento (CaseTasksSection, that case's
// tasks, firm-wide placements). The hook decides the scope from the surface.
//
// TWO FRAMES since the 190 glance (ii), RULED by Laurent: the user Bento stays
// the full-screen overlay above; the CASE Bento is an IN-PAGE PANEL — "make it
// a panel like the tasks panel, but more than double its width towards the
// edge of the events column next to it … it temporarily covers both tasks
// and events sections". A full-screen case Bento had no way out: its toggle
// sat under it. `frame="panel"` renders the same header row and board in a
// plain block the host places (CaseTasksSection spans its section across the
// grid's two columns while open); no portal, no backdrop, no outside-click
// close (the picker's menu is portalled and would count as outside) — the
// toggle stays reachable and Escape still closes.
import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import type { Dispatch } from "react";
import BentoBoard from "../BentoBoard";
import type { BentoCard } from "../BentoBoard";
import EisenTaskCard from "../EisenTaskCard";
import type { EisenCardTask } from "../EisenTaskCard";
import type { BentoState, BentoSurface } from "../../hooks/useBento";
import { bentoColumnOf } from "../../lib/crmDefinitions.mjs";
import { dayDiff, dayCountdownLabel } from "../../lib/dayCountdown";
import { EISEN_QUADRANTS } from "../../utils"; // 190 round 6 [T] — the quadrant palette, reused
import { isAbovePane } from "../../lib/paneStacking"; // 205 §5 — the Escape goes to whatever is in front
import type { EisenhowerDialogProps } from "./EisenhowerDialog";
import { FS, Z } from "../../lib/tokens";
import type { TFn } from "../../lib/taxonomyTypes";

/* global document, KeyboardEvent */

/** App's `userMatrixQuadrant` — the shape EisenhowerDialog already declares for the same prop (imported, 183's rule). */
type QuadrantOf = EisenhowerDialogProps["userMatrixQuadrant"];
interface Props {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly surface: Extract<BentoSurface, "user" | "case">;
  /** The surface's `useBento` state — the HOST's (it renders the picker beside its icon). */
  readonly bento: BentoState;
  readonly title: string;
  readonly tasks: readonly EisenCardTask[];
  readonly cases: readonly { id: number; title?: string; itemLinks?: unknown }[];
  readonly collectiveCases?: readonly { id: number; title?: string; itemLinks?: unknown }[];
  readonly tkTypes: readonly { code?: string | null; desc?: string }[];
  /** The viewer may move cards (the surface's right, 190 §E — the server enforces it too). */
  readonly canMove: boolean;
  readonly onCardClick: Dispatch<EisenCardTask>;
  readonly t: TFn;
  /** "dialog" (default): the full-screen overlay. "panel": an in-page block
   *  the host places — the case Bento (190 glance (ii)). */
  readonly frame?: "dialog" | "panel";
  /** The dialog frame's top edge — App's `headerH` (CSS px), the matrix's own. */
  readonly headerH?: number;
  /** 206E §1.1 — the dialog frame's LEFT edge: App's `sidebarEdge`, the right edge of whatever the
   *  sidebar occupies on screen (CSS px) — open, folded, the phone drawer, or 0 when it is hidden. */
  readonly sidebarEdge?: number;
  /** 190 round 6 [T] (ruled 2026-09-16, both hosts) — App's `userMatrixQuadrant(task)`, THE SAME
   *  FUNCTION EisenhowerDialog buckets with (EisenhowerDialog.tsx `tasksByQ[userMatrixQuadrant(t)]`):
   *  each card's left edge takes the accent of the quadrant that task sits in in his Eisenhower,
   *  from `EISEN_QUADRANTS` (utils.ts), so the two views are visibly one thing — and matching on
   *  that one function is what makes a disagreement between them impossible rather than unlikely.
   *  Absent, the edge is the Bento's own accent. */
  readonly quadrantOf?: QuadrantOf;
}

export default function BentoDialog({ open, onClose, surface, bento, title, tasks, cases, collectiveCases, tkTypes, canMove, onCardClick, t, frame = "dialog", headerH = 60, sidebarEdge = 0, quadrantOf }: Props) {
  // App.tsx:1410–1420 — the matrix's Escape, here.
  /* 205 §5 — AND IT DEFERS TO WHATEVER IS IN FRONT OF IT. `document` fires
     before every `window` listener (the hotkey chain, ModalLayout's own
     `escToClose`), so this closed the Bento out from under a dialog opened
     over it and the dialog closed a moment later on the same press — a phase
     ordering, which no `stopPropagation` could ever have reached.
     `isAbovePane()` (lib/paneStacking.ts) is 204 §1's own order asked at the
     keypress: a shell lifted to `Z.paneModal`/`paneModal2` over this pane, and
     Alt+F's SearchPopover, each register while they stand (that file's header
     measures who needs to and who does not). The
     `frame="panel"` mount (the case Bento, an inline region) is NOT a pane, so
     nothing ever lifts over it and nothing ever registers — its Escape is
     exactly what it was. */
  useEffect(() => {
    if (!open) return undefined;
    const h = (e: KeyboardEvent) => { if (e.key === "Escape" && !isAbovePane()) onClose(); };
    document.addEventListener("keydown", h);
    return () => { document.removeEventListener("keydown", h); };
  }, [open, onClose]);
  if (!open) return null;
  const movable: ReadonlySet<number> = new Set(canMove ? tasks.map((tk) => tk.id) : []);
  /* 190 glance (i)(c) — the widest day label and the widest type code on
     THIS board, for every card's two mirrored slots (EisenTaskCard
     `daysMirror` / `typeMirror`): the same clock the card reads. */
  const today = new Date().toISOString().slice(0, 10);
  let daysMirror: string | null = null;
  let typeMirror = "";
  for (const tk of tasks) {
    const label = dayCountdownLabel(dayDiff(tk.dueDate, today), t);
    if (label !== null && (daysMirror === null || label.length > daysMirror.length)) daysMirror = label;
    if (tk.type && tk.type.length > typeMirror.length) typeMirror = tk.type;
  }
  const cards: BentoCard[] = tasks.map((tk) => ({
    id: tk.id,
    column: bentoColumnOf(tk.id, bento.mine, bento.firm, bento.columns),
    node: (
      <EisenTaskCard
        task={tk}
        cases={cases}
        tkTypes={tkTypes}
        collectiveCases={collectiveCases}
        userMatrix={bento.placed}
        onCardClick={onCardClick}
        t={t}
        q={{ accent: quadrantOf ? (EISEN_QUADRANTS.find((qd) => qd.k === quadrantOf(tk))?.accent ?? "var(--ac)") : "var(--ac)" }}
        showMatter={surface !== "case"}
        daysMirror={daysMirror}
        typeMirror={typeMirror}
        /* [E] — two rows where the case name shows (My Bento); ONE row in a
           case's own Bento (every card is the case's — the name is hidden, so
           a first line would be empty), every card at the date block's height
           in both. */
        stacked={surface !== "case"}
        reserveDate
      />
    ),
  }));
  const stop = (e: React.SyntheticEvent) => { e.stopPropagation(); };
  const isPanel = frame === "panel";
  /* The overlay names itself; the panel's title is its section's ("Tasks",
     the lit toggle). The picker left this row for the icon's (190 [I]). */
  const headerRow = isPanel ? null : (
      <div
        role="presentation"
        onClick={stop}
        onKeyDown={stop}
        style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, flexShrink: 0 }}
      >
        <h2 style={{ margin: 0, fontSize: FS.s16, color: "var(--tp)", whiteSpace: "nowrap" }}>{title}</h2>
      </div>
  );
  const boardRow = (
      <div role="presentation" onClick={stop} onKeyDown={stop} style={{ flex: 1, display: "flex", minHeight: 0 }}>
        {bento.templateCode === null && (
          /* no template offers this surface: the board's place says so
             (the picker renders nothing — BentoPicker) */
          <span style={{ fontSize: FS.s11, color: "var(--tm)", fontStyle: "italic", padding: "12px 2px" }}>
            {t("bento.no_template_path", { fallback: "No Bento template offers this surface yet — Settings › Custom lists › Taxonomies › Bento Templates." })}
          </span>
        )}
        {bento.templateCode !== null && (
          <BentoBoard
            columns={bento.columns}
            cards={cards}
            movable={movable}
            onMove={bento.move}
            emptyLabel={t("bento.empty_column", { fallback: "No task here" })}
          />
        )}
      </div>
  );
  if (isPanel) {
    // The case matrix's own footprint (TaskMatrix.tsx: `minHeight: 360,
    // marginBottom: 12`), in the body slot the host gives it.
    return (
      <div className="bentoPanel" style={{ display: "flex", flexDirection: "column", minHeight: 360, marginBottom: 12 }}>
        {headerRow}
        {boardRow}
      </div>
    );
  }
  // PORTALLED to document.body: the user Bento is mounted by App beside the
  // matrix, and `position: fixed` is relative to the nearest transformed
  // ancestor — the portal takes the one door any host can use. React bubbles
  // a portal's events to the host's tree, so the backdrop stops its own click
  // after closing.
  return createPortal(
    <div
      role="presentation"
      style={{
        position: "fixed",
        top: headerH - 1,
        left: sidebarEdge, // 206E §1.1 — beside the sidebar, never over it
        right: 0,
        bottom: 0,
        background: "var(--bs)",
        zIndex: Z.pane,
        display: "flex",
        flexDirection: "column",
        padding: "20px 24px",
        overflow: "auto",
      }}
      onClick={(e) => { e.stopPropagation(); onClose(); }}
      onKeyDown={(e) => { if (e.key === "Escape") onClose(); }}
    >
      {headerRow}
      {boardRow}
    </div>,
    document.body,
  );
}
